import { useCallback, useState } from "react";
import ImportContext from "./context";
import { parse } from "papaparse";

const s2ab = (s: string) => {
  const buffer = new ArrayBuffer(s.length);
  const view = new Uint8Array(buffer);
  for (let i = 0; i < s.length; i++) {
    view[i] = s.charCodeAt(i) & 0xff;
  }

  return buffer;
};

const ImportContextWrapper = ({ children }: { children: React.ReactNode }) => {
  const [isUploading, setIsUploading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isError, setIsError] = useState(false);
  const [showImportMappingPage, setShowImportMappingPage] = useState(false);

  const [savedUploadedFile, setSavedUploadedFile] = useState<{
    name: string;
    data: string[][];
  }>({
    name: "",
    data: [],
  });

  const [csvFile, setCsvFile] = useState<{
    name: string;
    data: string[][];
  }>({
    name: "",
    data: [],
  });

  const onUploadCSV = useCallback((acceptedFiles: File[]) => {
    acceptedFiles.forEach(async (file) => {
      setIsUploading(true);
      let target: string;
      let result: string[][] = [];

      try {
        if (
          // https://stackoverflow.com/a/10246423/18110171
          file.type ===
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        ) {
          const arrayBuffer = await file.arrayBuffer();
          const data = new Uint8Array(arrayBuffer);

          // Dynamically import XLSX only when needed
          const XLSX = await import("xlsx/xlsx.mjs");

          const workbook = XLSX.read(data, { type: "array" });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          target = XLSX.utils.sheet_to_csv(worksheet);
        } else {
          target = await file.text();
        }

        result = parse<string[]>(target).data;
        // result after parsing has one or many empty strings.
        result = result.filter(
          (arr: string[]) => !arr.every((x: string) => x === ""),
        );

        setIsUploading(false);
        setCsvFile({ name: file.name, data: result });
        setSavedUploadedFile({ name: file.name, data: result });
        setIsSuccess(true);
      } catch (error) {
        console.error("Error processing file:", error);
        setIsError(true);
        setIsUploading(false);
      }
    });
  }, []);

  async function createExcelFileFromData(
    newData: unknown[][],
    tableName: string | undefined,
  ): Promise<File> {
    // Dynamically import XLSX only when this function is called
    const XLSX = await import("xlsx/xlsx.mjs");

    const worksheet = XLSX.utils.aoa_to_sheet(newData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, tableName);
    const wbout = XLSX.write(workbook, { bookType: "xlsx", type: "binary" });

    const blob = new Blob([s2ab(wbout)], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const fileName = `${tableName}.xlsx`;
    const now = new Date();
    return new File([blob], fileName, {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      lastModified: now.getTime(),
    });
  }

  const importContextData = {
    onUploadCSV,
    isUploading,
    setIsUploading,
    isSuccess,
    setIsSuccess,
    isError,
    setIsError,
    setShowImportMappingPage,
    showImportMappingPage,
    csvFile,
    setCsvFile,
    createExcelFileFromData,
    savedUploadedFile,
  };

  return (
    <ImportContext.Provider
      value={{
        ...importContextData,
      }}
    >
      {children}
    </ImportContext.Provider>
  );
};

export default ImportContextWrapper;
